import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { createApiClient } from "../api/client";
import { Family, SessionDto } from "../types";
import { track } from "../analytics";
import Modal from "./Modal";
import Button from "./ui/Button";

const MILES_TO_METERS = 1609.34;
const RADIUS_MILE_OPTIONS = [5, 10, 25, 50];

type DayPart = "Morning" | "Midday" | "Evening";
const DAY_PARTS: { value: DayPart; label: string; icon: string }[] = [
  { value: "Morning", label: "Morning", icon: "🌅" },
  { value: "Midday", label: "Midday", icon: "☀️" },
  { value: "Evening", label: "Evening", icon: "🌙" },
];

const textableCount = (f: Family) => f.members.filter((m) => m.mobileNumber).length;

const INPUT =
  "w-full rounded-xl border border-primary-200 bg-white/70 px-3.5 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200";

const Spinner = () => (
  <div className="flex justify-center py-10">
    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-500" />
  </div>
);

const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          /* clipboard may be unavailable; ignore */
        }
      }}
      className="rounded-full bg-cream-200 px-3 py-1.5 text-xs font-semibold text-ink ring-1 ring-primary-200/70 hover:bg-cream"
    >
      {copied ? "Copied!" : "Copy link"}
    </button>
  );
};

/** Done-step: show each member's invite link to send from the organizer's phone. */
const ShareInvites: React.FC<{
  family: Family;
  session: SessionDto;
  onClose: () => void;
  onAnother: () => void;
}> = ({ family, session, onClose, onAnother }) => {
  const navigate = useNavigate();
  const invitees = session.participants.filter((p) => p.inviteUrl);
  const goToPlan = () => {
    onClose();
    navigate(`/sessions/${session.id}`);
  };

  if (invitees.length === 0) {
    return (
      <div className="py-4 text-center">
        <p className="text-ink/70">No one in this family has a mobile number yet.</p>
        <p className="mt-1 text-sm text-ink/55">
          Add a number under <span className="font-medium">Manage</span>, then start again.
        </p>
        <Button fullWidth size="lg" className="mt-5" onClick={goToPlan}>
          Go to plan
        </Button>
      </div>
    );
  }

  return (
    <div>
      <p className="text-sm text-ink/60">
        Send each person their invite — tap <span className="font-medium text-ink/80">Text</span> to open
        Messages pre-filled, or copy the link to share anywhere.
      </p>
      <ul className="mt-3 space-y-2">
        {invitees.map((p) => {
          const member = family.members.find((m) => m.id === p.memberId);
          const name = member?.displayName || p.displayName || "Member";
          const number = member?.mobileNumber || "";
          const body = `${family.name}: what should we do tonight? Join — ${p.inviteUrl}`;
          const smsHref = `sms:${number}?&body=${encodeURIComponent(body)}`;
          return (
            <li key={p.id} className="rounded-2xl border border-primary-100 bg-cream-100 px-4 py-3">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate font-display font-semibold text-ink">{name}</p>
                  {number && <p className="truncate text-xs text-ink/50">{number}</p>}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {number && (
                    <a
                      href={smsHref}
                      className="rounded-full bg-primary-500 px-3 py-1.5 text-xs font-semibold text-white shadow-warm hover:bg-primary-600"
                    >
                      Text
                    </a>
                  )}
                  <CopyButton text={p.inviteUrl!} />
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      <div className="mt-5 flex flex-col gap-2">
        <Button fullWidth size="lg" onClick={goToPlan}>
          Go to plan
        </Button>
        <Button variant="ghost" onClick={onAnother}>
          Plan for another family
        </Button>
      </div>
    </div>
  );
};

/**
 * Two-step "start tonight's plan" flow in a single panel:
 *   1. pick a family   2. confirm location + radius and start
 * Then a share step: hand the organizer each member's invite link to send.
 * Optionally pre-selects a family (`initialFamilyId`) to jump straight to step 2.
 */
const StartPlanFlow: React.FC<{
  open: boolean;
  onClose: () => void;
  initialFamilyId?: string;
}> = ({ open, onClose, initialFamilyId }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [families, setFamilies] = useState<Family[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Family | null>(null);
  const [city, setCity] = useState("");
  const [radiusMi, setRadiusMi] = useState(25);
  const [dayPart, setDayPart] = useState<DayPart>("Evening");
  const [starting, setStarting] = useState(false);
  const [sent, setSent] = useState<SessionDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const { data } = await createApiClient(token).get<Family[]>("/api/families");
      setFamilies(data);
    } catch {
      setError("Couldn't load your families.");
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setQuery("");
    setSent(null);
    setError(null);
    load();
  }, [open, load]);

  const pick = useCallback((f: Family) => {
    setSelected(f);
    setSent(null);
    setError(null);
    setCity(f.city ?? "");
    setRadiusMi(f.searchRadiusMeters ? Math.round(f.searchRadiusMeters / MILES_TO_METERS) : 25);
    setDayPart("Evening");
    setConsent(false);
  }, []);

  useEffect(() => {
    if (open && initialFamilyId && !selected && families.length) {
      const f = families.find((x) => x.id === initialFamilyId);
      if (f) pick(f);
    }
  }, [open, initialFamilyId, families, selected, pick]);

  const filtered = useMemo(
    () => families.filter((f) => f.name.toLowerCase().includes(query.trim().toLowerCase())),
    [families, query]
  );

  const start = async () => {
    if (!selected) return;
    setStarting(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const { data } = await createApiClient(token).post<SessionDto>(
        `/api/families/${selected.id}/sessions`,
        {
          originCity: city.trim() || null,
          searchRadiusMeters: Math.round(radiusMi * MILES_TO_METERS),
          dayPart,
        }
      );
      setSent(data);
      track("plan_started", { day_part: dayPart });
    } catch {
      setError("Couldn't start the plan — please try again.");
    } finally {
      setStarting(false);
    }
  };

  const screen = loading
    ? "loading"
    : error && !families.length
    ? "error"
    : families.length === 0
    ? "empty"
    : sent
    ? "done"
    : !selected
    ? "list"
    : "confirm";

  const title = sent ? "Share the invites" : selected ? "Confirm & start" : "Start tonight's plan";

  return (
    <Modal open={open} onClose={onClose} title={title}>
      <AnimatePresence mode="wait">
        <motion.div
          key={screen}
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -10 }}
          transition={{ duration: 0.2 }}
        >
          {screen === "loading" && <Spinner />}

          {screen === "error" && <p className="text-ink/70">{error}</p>}

          {screen === "empty" && (
            <div className="py-6 text-center">
              <p className="text-ink/70">You don't have any families yet.</p>
              <Link to="/dashboard" onClick={onClose} className="mt-2 inline-block font-medium text-primary-600">
                Add one →
              </Link>
            </div>
          )}

          {screen === "done" && sent && selected && (
            <ShareInvites
              family={selected}
              session={sent}
              onClose={onClose}
              onAnother={() => {
                setSelected(null);
                setSent(null);
              }}
            />
          )}

          {screen === "list" && (
            <div>
              <p className="mb-3 text-sm text-ink/55">Who's deciding tonight?</p>
              {families.length > 5 && (
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search families…"
                  className={`${INPUT} mb-3`}
                />
              )}
              <ul className="max-h-[55vh] space-y-2 overflow-y-auto">
                {filtered.map((f) => (
                  <li key={f.id}>
                    <button
                      onClick={() => pick(f)}
                      className="flex w-full items-center justify-between rounded-2xl border border-primary-100 bg-cream-100 px-4 py-3.5 text-left transition-all hover:border-primary-300 hover:bg-cream-200"
                    >
                      <span>
                        <span className="block font-display text-base font-semibold text-ink">{f.name}</span>
                        <span className="block text-xs text-ink/55">
                          {f.members.length} {f.members.length === 1 ? "person" : "people"} · {textableCount(f)} textable
                        </span>
                      </span>
                      <span className="text-xl leading-none text-primary-400">›</span>
                    </button>
                  </li>
                ))}
                {filtered.length === 0 && <li className="py-2 text-sm text-ink/40">No matches.</li>}
              </ul>
            </div>
          )}

          {screen === "confirm" && selected && (
            <div className="space-y-3.5">
              <button
                onClick={() => setSelected(null)}
                className="flex items-center gap-1 text-sm text-ink/55 transition-colors hover:text-ink"
              >
                ‹ <span className="font-medium text-ink/80">{selected.name}</span>
              </button>
              <p className="text-sm font-medium text-ink/80">Where are you tonight, and how far to look?</p>
              <input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Origin city (e.g. Austin, TX)"
                className={INPUT}
              />
              <select value={radiusMi} onChange={(e) => setRadiusMi(Number(e.target.value))} className={INPUT}>
                {RADIUS_MILE_OPTIONS.map((mi) => (
                  <option key={mi} value={mi}>
                    Within {mi} miles
                  </option>
                ))}
              </select>
              <div>
                <p className="mb-1.5 text-sm font-medium text-ink/80">When tonight?</p>
                <div className="grid grid-cols-3 gap-2">
                  {DAY_PARTS.map((dp) => (
                    <button
                      key={dp.value}
                      type="button"
                      onClick={() => setDayPart(dp.value)}
                      className={`rounded-xl border px-2 py-2.5 text-sm font-medium transition-colors ${
                        dayPart === dp.value
                          ? "border-primary-500 bg-primary-50 text-primary-700 ring-1 ring-primary-300"
                          : "border-primary-200 bg-white/70 text-ink/70 hover:border-primary-300"
                      }`}
                    >
                      <span className="mr-1">{dp.icon}</span>
                      {dp.label}
                    </button>
                  ))}
                </div>
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <label className="flex items-start gap-2.5 rounded-xl bg-cream-100 p-3 text-xs text-ink/70">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-primary-300 text-primary-500 focus:ring-primary-300"
                />
                <span>
                  I confirm everyone I'm inviting has agreed to receive Kinoes text messages about this plan.
                  They can reply STOP at any time. See our{" "}
                  <Link to="/sms-policy" onClick={onClose} className="underline hover:text-primary-600">
                    SMS Policy
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" onClick={onClose} className="underline hover:text-primary-600">
                    Privacy Policy
                  </Link>
                  .
                </span>
              </label>
              <Button fullWidth size="lg" onClick={start} disabled={starting || !consent}>
                {starting ? "Starting…" : "Start & get invite links"}
              </Button>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </Modal>
  );
};

export default StartPlanFlow;
